import logo from "../../assets/svg/logo.svg";
import styled from "styled-components";
import colours from "../../lib/colours";
import { AppName } from "../../lib/constants";
import { Link, useLocation, useNavigate } from "react-router-dom";
import appConfig from "../../lib/appConfig";
import { useAuthenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { FilledButtonAlt2 } from "../atoms/FilledButton";
import {
  Menu,
  MenuItem,
  MenuButton,
  MenuHeader,
  MenuDivider,
} from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import { ReactComponent as SignoutIcon } from "../../assets/svg/signout.svg";
import { ReactComponent as UserIcon } from "../../assets/svg/user.svg";
import useWindowDimensions from "../../utils/useWindowDimensions";
import { useCallback, useEffect } from "react";

const HeaderContainer = styled.header`
  background-color: ${colours.th1};
  height: ${appConfig.headerHeight}px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  color: white;
  align-items: center;
  @media (max-width: ${appConfig.smallMobileWidth}px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Logo = styled.img`
  height: 50px;
  margin-left: 25px;
  @media (max-width: ${appConfig.mobileWidth}px) {
    height: 40px;
  }
`;

const HomeLink = styled(Link)`
  margin: auto;
  padding-inline: 20px;
  font-size: 26px;
  font-weight: 600;
  color: ${colours.white};
  text-decoration: none;
  @media (max-width: ${appConfig.tabletWidth}px) {
    font-size: 22px;
  }
  @media (max-width: ${appConfig.mobileWidth}px) {
    font-size: 18px;
  }
  @media (max-width: ${appConfig.smallMobileWidth}px) {
    display: none;
  }
`;

const RightSection = styled.section`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
`;

const SignInContainer = styled.div`
  margin-right: 25px;
`;

const StyledMenuButton = styled(MenuButton)`
  height: 100%;
  max-width: 400px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  color: ${colours.white};
  background-color: ${colours.th1};
  border: none;
  font-size: 14px;
  cursor: pointer;
  padding: 10px 16px;
  font-weight: 400;
  transition: background-color 0.2s linear, color 0.2s linear;
  &:hover,
  &:active {
    background-color: ${colours.th1l1};
  }
`;

const StyledMenu = styled(Menu)`
  & ul {
    border-radius: 0;
    background-color: ${colours.th1l2};
    right: 0;
    left: unset !important;
  }
`;

const StyledMenuHeader = styled(MenuHeader)`
  color: ${colours.th1l5} !important;
  padding-block: 12px !important;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 300;
  text-transform: lowercase;
`;

const UserIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 99px !important;
  height: 32px;
  width: 32px;
  background-color: ${colours.th1l3}99;
  color: ${colours.white};
  margin-right: 12px;
`;

const StyledMenuDivider = styled(MenuDivider)`
  background-color: ${colours.th1l3}66 !important;
`;

const StyledMenuItem = styled(MenuItem)`
  color: ${colours.white};
  font-weight: 400;
  &:hover,
  &:active,
  &:focus {
    background-color: ${colours.th1l1};
  }
`;

function Header() {
  const { user, signOut } = useAuthenticator((context) => [context.user]);
  const navigate = useNavigate();
  const location = useLocation();
  const { width } = useWindowDimensions();

  const isMobile = width <= appConfig.mobileWidth;
  const userName = user?.signInDetails?.loginId || "Unknown User";

  const goToSignIn = useCallback(() => {
    navigate("/signin", { state: { prevLocation: location.pathname } });
  }, [location, navigate]);

  const onSignOut = () => {
    signOut();
  };

  const onAdminNavigate = () => {
    navigate("/admin");
  };

  useEffect(() => {
    if (!user && location.pathname === "/admin") {
      goToSignIn();
    }
  }, [user, location, goToSignIn]);

  const renderUserMenuButton = () => {
    return isMobile ? (
      <StyledMenuButton>
        <UserIcon />
      </StyledMenuButton>
    ) : (
      <StyledMenuButton>{userName}</StyledMenuButton>
    );
  };

  const renderUserMenu = () => {
    return (
      <StyledMenu menuButton={renderUserMenuButton()}>
        {isMobile && (
          <StyledMenuHeader>
            <UserIconContainer>
              <UserIcon style={{ height: "16px" }} />
            </UserIconContainer>
            {userName}
          </StyledMenuHeader>
        )}
        {isMobile && <StyledMenuDivider />}
        <StyledMenuItem onClick={onAdminNavigate}>
          Export report data
        </StyledMenuItem>
        <StyledMenuDivider />
        <StyledMenuItem onClick={onSignOut}>
          <SignoutIcon style={{ marginRight: "16px" }} />
          Sign out
        </StyledMenuItem>
      </StyledMenu>
    );
  };

  return (
    <HeaderContainer>
      <a
        href="https://deeca.vic.gov.au"
        target="_blank"
        title="Department of Energy, Environment and Climate Action"
        rel="noopener noreferrer"
      >
        <Logo
          src={logo}
          alt="Department of Energy, Environment and Climate Action Logo"
        />
      </a>

      <HomeLink to="/" title="Home - NVR Map">
        {AppName}
      </HomeLink>

      <RightSection>
        {user && user.username ? (
          renderUserMenu()
        ) : (
          <SignInContainer>
            <FilledButtonAlt2 title="DEECA Access" onClick={goToSignIn}>
              DEECA Access
            </FilledButtonAlt2>
          </SignInContainer>
        )}
      </RightSection>
    </HeaderContainer>
  );
}

export default Header;
